import React, { useState, useCallback, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MuniDetailFieldPlaceholder from './MuniDetailFieldPlaceholder.js';
import {
  colors,
  muniDetailInputShellSx,
} from './constants.js';

const NOTES_INPUT_ROWS = 2;
const MUNI_NOTES_PLACEHOLDER =
  'Ex: Sample Assessor is a bad negotiator, can often settle at or sometimes even below workup values. They focus on matching style types, view GLA as less important';

function MunicipalityNotesInput({ value, onChange, isSaving = false, loading = false }) {
  const textareaRef = useRef(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  const updateScrollIndicator = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      setHasMoreBelow(false);
      return;
    }
    const overflow = el.scrollHeight > el.clientHeight + 2;
    const notAtBottom = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
    setHasMoreBelow(overflow && notAtBottom);
  }, []);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return undefined;

    updateScrollIndicator();
    el.addEventListener('scroll', updateScrollIndicator, { passive: true });
    return () => {
      el.removeEventListener('scroll', updateScrollIndicator);
    };
  }, [value, updateScrollIndicator]);

  if (loading) {
    return <MuniDetailFieldPlaceholder />;
  }

  return (
    <Box sx={{ position: 'relative', flex: 1, minWidth: 0, pb: 0.25, m: 0 }}>
      <Box sx={muniDetailInputShellSx()}>
        <TextField
          fullWidth
          size="small"
          placeholder={MUNI_NOTES_PLACEHOLDER}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          variant="outlined"
          multiline
          rows={NOTES_INPUT_ROWS}
          maxRows={NOTES_INPUT_ROWS}
          inputRef={textareaRef}
          InputProps={{
            inputComponent: 'textarea',
            sx: {
              padding: 0,
              alignItems: 'flex-start',
              '& textarea': {
                padding: '6px 8px',
                boxSizing: 'border-box',
                overflow: 'auto',
                resize: 'none',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                scrollbarWidth: 'thin',
                '&::placeholder': {
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  opacity: 1,
                  color: colors.textTertiary,
                },
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: 3,
                  backgroundColor: 'rgba(0,0,0,0.18)',
                },
              },
            },
          }}
          sx={{
            m: 0,
            p: 0,
            width: '100%',
            display: 'block',
            '& .MuiOutlinedInput-root': {
              p: 0,
              m: 0,
              bgcolor: 'transparent',
              '& fieldset': { border: 'none' },
            },
          }}
        />
      </Box>
      {hasMoreBelow && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 32,
            pointerEvents: 'none',
            background: `linear-gradient(180deg, transparent 0%, ${colors.bgPrimary} 72%)`,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            pb: 0.25,
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: colors.accent,
              m: 0,
              opacity: 0.9,
            }}
          >
            Scroll for more
          </Typography>
        </Box>
      )}
      {isSaving && (
        <Typography
          sx={{
            position: 'absolute',
            right: 8,
            bottom: 4,
            fontSize: 10,
            color: colors.textTertiary,
            pointerEvents: 'none',
            lineHeight: 1,
          }}
        >
          Saving...
        </Typography>
      )}
    </Box>
  );
}

export default React.memo(MunicipalityNotesInput);
